/**

// Initialize logger for this module
const logger = window.Logger ? window.Logger.create('categories') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};
 * Category structure configuration for UsedFindr
 * Defines hierarchical main categories and subcategories
 * This should match the backend category structure
 */

const CATEGORY_STRUCTURE = {
  'Appliances': [
    'Kitchen',
    'Vacuums',
    'Home Comfort',
  ],
  'Electronics': [
    'Computers & Laptops',
    'TV & Home Theater',
    'Mobile & Wearables',
    'Gaming',
    'Cameras & Drones',
  ],
  'Power Tools': [
    'Tool Combo Kits',
    'Drills & Driver Kits',
    'Saws',
    'Sanders & Grinders',
    'Outdoor Power Equipment',
  ],
  'Home & Garden': [
    'Furniture',
    'Outdoor',
    'Home Improvement',
  ],
  'Sports & Outdoors': [
    'Bicycles & E-Bikes',
    'Exercise Equipment',
    'Camping & Hiking Gear',
    'Golf Clubs',
    'Kayaks & Paddleboards',
  ],
  'Fashion & Apparel': [
    'Luxury & Designer Handbags',
    'Watches',
    'High-End Sneakers',
    'Outerwear',
    'Suitcases & Luggage',
  ],
  'Other High-Value': [
    'Musical Instruments',
    'Collectibles',
  ],
};

// Legacy category mapping for backward compatibility
const LEGACY_CATEGORY_MAPPING = {
  'TVs': ['Electronics', 'TV & Home Theater'],
  'Coffee Makers': ['Appliances', 'Kitchen'],
  'Laptops': ['Electronics', 'Computers & Laptops'],
  'Vacuums': ['Appliances', 'Vacuums'],
  'DVDs': ['Electronics', 'TV & Home Theater'],
  'Headphones & Audio': ['Electronics', 'Mobile & Wearables'],
  'Cameras & Lenses': ['Electronics', 'Cameras & Drones'],
  'Smartphones': ['Electronics', 'Mobile & Wearables'],
  'Power Tools': ['Power Tools', 'Tool Combo Kits'],
};

/**
 * Category Manager - Singleton class for managing category operations
 */
class CategoryManager {
  constructor() {
    if (CategoryManager.instance) {
      return CategoryManager.instance;
    }
    this.categoryStructure = CATEGORY_STRUCTURE;
    this.legacyMapping = LEGACY_CATEGORY_MAPPING;
    CategoryManager.instance = this;
  }
  
  /**
   * Get list of all main category names
   * @returns {string[]} Array of main category names
   */
  getMainCategories() {
    return Object.keys(this.categoryStructure);
  }
  
  /**
   * Get list of subcategories for a given main category
   * @param {string} mainCategory - Name of the main category
   * @returns {string[]} Array of subcategory names, empty array if not found
   */
  getSubcategories(mainCategory) {
    return this.categoryStructure[mainCategory] || [];
  }
  
  /**
   * Validate a main category and optional subcategory
   * @param {string} mainCategory - Main category name
   * @param {string} subcategory - Subcategory name (optional)
   * @returns {{valid: boolean, error: string|null}} Validation result
   */
  validateCategoryPair(mainCategory, subcategory = null) {
    if (!mainCategory) {
      return { valid: false, error: 'Main category is required' };
    }
    
    if (!this.categoryStructure[mainCategory]) {
      return { valid: false, error: `Invalid main category: ${mainCategory}` };
    }
    
    // Subcategory is optional
    if (subcategory) {
      const validSubcategories = this.categoryStructure[mainCategory];
      if (!validSubcategories.includes(subcategory)) {
        return { 
          valid: false, 
          error: `Invalid subcategory '${subcategory}' for main category '${mainCategory}'` 
        };
      }
    }
    
    return { valid: true, error: null };
  }
  
  /**
   * Get the full category structure
   * @returns {Object} Complete category structure
   */
  getCategoryStructure() {
    return { ...this.categoryStructure };
  }
  
  /**
   * Map a legacy flat category to new hierarchical structure
   * @param {string} legacyCategory - Old category name
   * @returns {Array|null} [mainCategory, subcategory] or null if not found
   */
  mapLegacyCategory(legacyCategory) {
    return this.legacyMapping[legacyCategory] || null;
  }
  
  /**
   * Populate a subcategory dropdown based on selected main category
   * @param {string} mainCategory - Selected main category
   * @param {HTMLSelectElement} subcategorySelect - The subcategory select element
   * @param {string} selectedSubcategory - Optional subcategory to preselect
   */
  populateSubcategoryDropdown(mainCategory, subcategorySelect, selectedSubcategory = null) {
    if (!subcategorySelect) {
      logger.error('Subcategory select element not found');
      return;
    }
    
    // Clear existing options
    subcategorySelect.innerHTML = '<option value="">Select sub-category (optional)</option>';
    
    if (!mainCategory) {
      subcategorySelect.disabled = true;
      return;
    }
    
    const subcategories = this.getSubcategories(mainCategory);
    if (subcategories.length === 0) {
      subcategorySelect.disabled = true;
      return;
    }
    
    // Enable and populate
    subcategorySelect.disabled = false;
    subcategories.forEach(subcat => {
      const option = document.createElement('option');
      option.value = subcat;
      option.textContent = subcat;
      if (selectedSubcategory && subcat === selectedSubcategory) {
        option.selected = true;
      }
      subcategorySelect.appendChild(option);
    });
  }
  
  /**
   * Get category display string for UI
   * @param {string} mainCategory - Main category
   * @param {string} subcategory - Subcategory (optional)
   * @returns {string} Formatted category string
   */
  getCategoryDisplayString(mainCategory, subcategory = null) {
    if (!mainCategory) {
      return 'Uncategorized';
    }
    if (subcategory) {
      return `${mainCategory} > ${subcategory}`;
    }
    return mainCategory;
  }
  
  /**
   * Setup cascading dropdowns for a form
   * @param {string} mainSelectId - ID of main category select element
   * @param {string} subSelectId - ID of subcategory select element
   * @param {Object} preselected - Optional preselected values {mainCategory, subcategory}
   */
  setupCascadingDropdowns(mainSelectId, subSelectId, preselected = {}) {
    const mainSelect = document.getElementById(mainSelectId);
    const subSelect = document.getElementById(subSelectId);
    
    if (!mainSelect || !subSelect) {
      logger.error('Cascading dropdown elements not found');
      return;
    }
    
    // Populate main category dropdown
    mainSelect.innerHTML = '<option value="">Select a category</option>';
    this.getMainCategories().forEach(mainCat => {
      const option = document.createElement('option');
      option.value = mainCat;
      option.textContent = mainCat;
      if (preselected.mainCategory && mainCat === preselected.mainCategory) {
        option.selected = true;
      }
      mainSelect.appendChild(option);
    });
    
    // Setup change event listener
    mainSelect.addEventListener('change', (e) => {
      const selectedMain = e.target.value;
      this.populateSubcategoryDropdown(selectedMain, subSelect);
    });
    
    // Initialize subcategory dropdown based on preselected main category
    if (preselected.mainCategory) {
      this.populateSubcategoryDropdown(
        preselected.mainCategory, 
        subSelect, 
        preselected.subcategory
      );
    } else {
      subSelect.disabled = true;
    }
  }
  
  /**
   * Get category icon/emoji for a main category
   * @param {string} mainCategory - Main category name
   * @returns {string} Icon/emoji string
   */
  getCategoryIcon(mainCategory) {
    const icons = {
      'Appliances': '🏠',
      'Electronics': '💻',
      'Power Tools': '🔨',
      'Home & Garden': '🌿',
      'Sports & Outdoors': '⚽',
      'Fashion & Apparel': '👜',
      'Other High-Value': '🎵',
    };
    return icons[mainCategory] || '📦';
  }
  
  /**
   * Get subcategory icon/emoji
   * @param {string} mainCategory - Main category name
   * @param {string} subcategory - Subcategory name
   * @returns {string} Icon/emoji string
   */
  getSubcategoryIcon(mainCategory, subcategory) {
    const icons = {
      'Appliances': {
        'Kitchen': '☕',
        'Vacuums': '🧹',
        'Home Comfort': '❄️',
      },
      'Electronics': {
        'Computers & Laptops': '💻',
        'TV & Home Theater': '📺',
        'Mobile & Wearables': '📱',
        'Gaming': '🎮',
        'Cameras & Drones': '📷',
      },
      'Power Tools': {
        'Tool Combo Kits': '🧰',
        'Drills & Driver Kits': '🔩',
        'Saws': '⚙️',
        'Sanders & Grinders': '🔧',
        'Outdoor Power Equipment': '🌱',
      },
      'Home & Garden': {
        'Furniture': '🪑',
        'Outdoor': '🌳',
        'Home Improvement': '🔨',
      },
      'Sports & Outdoors': {
        'Bicycles & E-Bikes': '🚴',
        'Exercise Equipment': '🏋️',
        'Camping & Hiking Gear': '⛺',
        'Golf Clubs': '⛳',
        'Kayaks & Paddleboards': '🚣',
      },
      'Fashion & Apparel': {
        'Luxury & Designer Handbags': '👜',
        'Watches': '⌚',
        'High-End Sneakers': '👟',
        'Outerwear': '🧥',
        'Suitcases & Luggage': '🧳',
      },
      'Other High-Value': {
        'Musical Instruments': '🎸',
        'Collectibles': '💎',
      },
    };
    
    return icons[mainCategory]?.[subcategory] || '📦';
  }
}

// Create and export a singleton instance
const categoryManager = new CategoryManager();

// For backward compatibility, also export individual functions
function getMainCategories() {
  return categoryManager.getMainCategories();
}

function getSubcategories(mainCategory) {
  return categoryManager.getSubcategories(mainCategory);
}

function validateCategoryPair(mainCategory, subcategory) {
  return categoryManager.validateCategoryPair(mainCategory, subcategory);
}

function getCategoryStructure() {
  return categoryManager.getCategoryStructure();
}

function mapLegacyCategory(legacyCategory) {
  return categoryManager.mapLegacyCategory(legacyCategory);
}

function populateSubcategoryDropdown(mainCategory, subcategorySelect, selectedSubcategory) {
  return categoryManager.populateSubcategoryDropdown(mainCategory, subcategorySelect, selectedSubcategory);
}

function getCategoryDisplayString(mainCategory, subcategory) {
  return categoryManager.getCategoryDisplayString(mainCategory, subcategory);
}

function setupCascadingDropdowns(mainSelectId, subSelectId, preselected) {
  return categoryManager.setupCascadingDropdowns(mainSelectId, subSelectId, preselected);
}

function getCategoryIcon(mainCategory) {
  return categoryManager.getCategoryIcon(mainCategory);
}

function getSubcategoryIcon(mainCategory, subcategory) {
  return categoryManager.getSubcategoryIcon(mainCategory, subcategory);
}

// Export for module usage
export { CategoryManager, categoryManager };

